import { useCallback, useContext, useMemo, useState, type PropsWithChildren } from 'react'
import type { Goal, TimelineItem } from '../types/timeline'
import { toDate } from '../utils/timeUtils'
import { DataContext } from './dataContextCore'
import { CanvasScaleContext, type CanvasScaleContextValue } from './canvasScaleContextCore'

const DAY_MS = 24 * 60 * 60 * 1000
const MIN_WINDOW_MS = 7 * DAY_MS
const FALLBACK_WINDOW_MS = 365 * DAY_MS
let lastInvalidGoalSignature = ''

const addYears = (date: Date, years: number) =>
  new Date(date.getFullYear() + years, date.getMonth(), date.getDate(), date.getHours(), date.getMinutes())

/**
 * Resilient version of `toDate`. Returns null for any value that cannot be
 * parsed into a real Date (undefined, null, empty string, malformed string,
 * etc). Use this in places that must NOT blank the entire React tree just
 * because one legacy/anonymous-merged goal has a missing date.
 */
const tryToDate = (value: unknown): Date | null => {
  if (value === undefined || value === null || value === '') return null
  try {
    return toDate(value as Parameters<typeof toDate>[0])
  } catch {
    return null
  }
}

const getAbsoluteBounds = (goals: readonly Goal[]) => {
  const emptyFallback = () => {
    const today = new Date()
    return {
      absoluteStart: addYears(today, -1),
      absoluteEnd: addYears(today, 2),
    }
  }

  if (goals.length === 0) return emptyFallback()

  const starts: number[] = []
  const ends: number[] = []
  const droppedIds: string[] = []
  let droppedCount = 0
  for (const goal of goals) {
    const start = tryToDate(goal.startDate)
    const end = tryToDate(goal.endDate)
    if (!start || !end) {
      droppedCount += 1
      droppedIds.push(goal.id)
      continue
    }
    starts.push(start.getTime())
    ends.push(end.getTime())
  }

  if (droppedCount > 0) {
    const signature = droppedIds.sort().join('|')
    if (signature !== lastInvalidGoalSignature) {
      lastInvalidGoalSignature = signature
      console.warn(
        `[CanvasScaleProvider] Skipped ${droppedCount} goal(s) with invalid start/end dates. Check the legacy useData().goals store for orphaned/anonymous-merged entries.`,
      )
    }
  } else {
    lastInvalidGoalSignature = ''
  }

  if (starts.length === 0) return emptyFallback()

  return {
    absoluteStart: addYears(new Date(Math.min(...starts)), -1),
    absoluteEnd: addYears(new Date(Math.max(...ends)), 2),
  }
}

const clampWindow = (
  startMs: number,
  endMs: number,
  absoluteStartMs: number,
  absoluteEndMs: number,
) => {
  const absoluteDuration = absoluteEndMs - absoluteStartMs
  const requestedDuration = Math.max(MIN_WINDOW_MS, endMs - startMs)
  const duration = Math.min(requestedDuration, absoluteDuration)
  let nextStart = startMs
  let nextEnd = nextStart + duration

  if (nextStart < absoluteStartMs) {
    nextStart = absoluteStartMs
    nextEnd = nextStart + duration
  }

  if (nextEnd > absoluteEndMs) {
    nextEnd = absoluteEndMs
    nextStart = nextEnd - duration
  }

  return {
    start: new Date(nextStart),
    end: new Date(nextEnd),
  }
}

const EMPTY_GOALS: Goal[] = []

export const CanvasScaleProvider = ({ children }: PropsWithChildren) => {
  const dataContext = useContext(DataContext)
  const rawGoals = dataContext?.goals
  const goals = useMemo(() => rawGoals ?? EMPTY_GOALS, [rawGoals])
  const { absoluteStart, absoluteEnd } = useMemo(() => getAbsoluteBounds(goals), [goals])
  const [focusedItemId, setFocusedItemId] = useState<string | null>(null)
  const [customWindow, setCustomWindow] = useState<{ start: Date; end: Date } | null>(null)

  const absoluteStartMs = absoluteStart.getTime()
  const absoluteEndMs = absoluteEnd.getTime()

  const effectiveWindow = useMemo(() => {
    if (!customWindow) {
      if (goals.length === 0) {
        const today = new Date()
        return {
          start: addYears(today, -1),
          end: new Date(today.getTime() + FALLBACK_WINDOW_MS),
        }
      }
      return clampWindow(absoluteStartMs, absoluteEndMs, absoluteStartMs, absoluteEndMs)
    }
    return clampWindow(
      customWindow.start.getTime(),
      customWindow.end.getTime(),
      absoluteStartMs,
      absoluteEndMs,
    )
  }, [customWindow, goals.length, absoluteStartMs, absoluteEndMs])

  const effectiveFocusedItemId = goals.length === 0 ? null : focusedItemId

  const setViewWindow = useCallback(
    (startDate: Date, endDate: Date) => {
      setCustomWindow(
        clampWindow(startDate.getTime(), endDate.getTime(), absoluteStartMs, absoluteEndMs),
      )
    },
    [absoluteEndMs, absoluteStartMs],
  )

  const resetViewWindow = useCallback(() => {
    setFocusedItemId(null)
    setCustomWindow(null)
  }, [])

  const zoomAt = useCallback(
    (anchorRatio: number, zoomFactor: number) => {
      const startMs = effectiveWindow.start.getTime()
      const endMs = effectiveWindow.end.getTime()
      const currentDuration = endMs - startMs
      const nextDuration = currentDuration * zoomFactor
      const boundedAnchorRatio = Math.min(1, Math.max(0, anchorRatio))
      const anchorMs = startMs + currentDuration * boundedAnchorRatio
      const nextStart = anchorMs - nextDuration * boundedAnchorRatio
      const nextEnd = nextStart + nextDuration

      setCustomWindow(clampWindow(nextStart, nextEnd, absoluteStartMs, absoluteEndMs))
    },
    [absoluteEndMs, absoluteStartMs, effectiveWindow],
  )

  const panByRatio = useCallback(
    (ratioDelta: number) => {
      const startMs = effectiveWindow.start.getTime()
      const endMs = effectiveWindow.end.getTime()
      const duration = endMs - startMs
      const shift = duration * ratioDelta

      setCustomWindow(clampWindow(startMs + shift, endMs + shift, absoluteStartMs, absoluteEndMs))
    },
    [absoluteEndMs, absoluteStartMs, effectiveWindow],
  )

  const focusItem = useCallback(
    (itemId: string, item: TimelineItem) => {
      const itemStart = tryToDate(item.startDate)
      const itemEnd = tryToDate(item.endDate)
      if (!itemStart || !itemEnd) {
        // Defensive: never throw from a click handler. Focus the id so the
        // selection still moves, but leave the view window untouched.
        console.warn(
          `[CanvasScaleProvider] focusItem skipped window update — item "${itemId}" has invalid dates.`,
        )
        setFocusedItemId(itemId)
        return
      }
      const itemStartMs = itemStart.getTime()
      const itemEndMs = itemEnd.getTime()
      const itemDuration = Math.max(DAY_MS, itemEndMs - itemStartMs)
      const visibleDuration = Math.max(MIN_WINDOW_MS, itemDuration / 0.9)
      const margin = visibleDuration * 0.05

      setFocusedItemId(itemId)
      setCustomWindow(
        clampWindow(itemStartMs - margin, itemEndMs + margin, absoluteStartMs, absoluteEndMs),
      )
    },
    [absoluteEndMs, absoluteStartMs],
  )

  const value = useMemo<CanvasScaleContextValue>(
    () => ({
      viewWindowStart: effectiveWindow.start,
      viewWindowEnd: effectiveWindow.end,
      focusedItemId: effectiveFocusedItemId,
      minWindowMs: MIN_WINDOW_MS,
      absoluteStart,
      absoluteEnd,
      setFocusedItemId,
      setViewWindow,
      zoomAt,
      panByRatio,
      focusItem,
      resetViewWindow,
    }),
    [
      absoluteEnd,
      absoluteStart,
      effectiveFocusedItemId,
      effectiveWindow.end,
      effectiveWindow.start,
      focusItem,
      panByRatio,
      resetViewWindow,
      setViewWindow,
      zoomAt,
    ],
  )

  return <CanvasScaleContext.Provider value={value}>{children}</CanvasScaleContext.Provider>
}
